import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import type { MenuItem } from '@openng/optimus-ui/api';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { MenuModule } from '@openng/optimus-ui/menu';
import { SelectButton } from '@openng/optimus-ui/selectbutton';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UserService } from '@app/auth/services/user.service';
import { LearningHubService } from '@app/modules/learning-hub/services/learning-hub.service';
import { IDE_PROVIDERS } from '@app/modules/settings/components/platform-settings/platform-settings-integrations/platform-settings-integrations.component';
import { MODULE_ROUTE_PATH, PAGE_ROUTE_PATH } from '@app/shared/constants/shared.constant';
import type {
    IdeConfigs,
    PlatformIntegrationAdapter,
} from '@app/shared/models/platform-settings-integration.model';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { ClipboardService } from '@app/shared/services/clipboard.service';
import { PlatformSettingService } from '@app/shared/services/platform-setting.service';
import { ThemeService } from '@app/shared/services/theme.service';
import { WindowService } from '@app/shared/services/window.service';
import { Helpers } from '@app/shared/utils/helpers';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';

@Component({
    selector: 'cor-profile-popover',
    templateUrl: './profile-popover.component.html',
    imports: [
        FormsModule,
        NgTemplateOutlet,
        AvatarModule,
        RouterLink,
        RouterLinkActive,
        MenuModule,
        SelectButton,
        TooltipModule,
        NgStyle,
        NgClass,
        UniqueRgbColorPipe,
    ],
})
export class ProfilePopoverComponent implements OnInit {
    readonly avatar = input(false);

    private readonly integrations = signal<PlatformIntegrationAdapter[]>([]);

    protected readonly authService = inject(AuthenticationService);
    protected readonly themeService = inject(ThemeService);
    private readonly userService = inject(UserService);
    private readonly clipboardService = inject(ClipboardService);
    private readonly learningHubService = inject(LearningHubService);
    private readonly platformSettingService = inject(PlatformSettingService);
    private readonly windowService = inject(WindowService);
    private readonly destroyRef = inject(DestroyRef);
    protected appConfigService = inject(AppConfigService);

    protected profileApiKey = {
        copyStatus: signal<'isIdle' | 'isLoading' | 'isSuccess' | 'isError'>('isIdle'),
        apiKey: toSignal(
            this.authService.currentUser$.pipe(
                filter(user => !!user?.id),
                switchMap(user =>
                    this.userService.getById(user.id).pipe(
                        map(({ result }) => result.apiKey ?? null),
                        catchError(() => of(null)),
                    ),
                ),
            ),
            { initialValue: null },
        ),
    };

    protected readonly profilePic = computed(() => {
        const currentUser = this.authService.currentUser();
        return Helpers.getInitialsFromName(currentUser?.visibleName || currentUser?.name || '');
    });

    protected readonly productFruitsId = computed(
        () => this.appConfigService.appConfig().productFruits,
    );

    protected readonly ideMenuItems = computed((): MenuItem[] =>
        this.integrations()
            .filter(integration => integration.name in IDE_PROVIDERS)
            .map(integration => {
                const configs = integration.configs as IdeConfigs;
                return {
                    label: IDE_PROVIDERS[integration.name].displayName,
                    icon: `${IDE_PROVIDERS[integration.name].logoClass} tw:text-[1.25rem]`,
                    command: () =>
                        this.windowService.openInNewTab(
                            this.getIDEUrl(configs.url, configs.queryParams),
                        ),
                };
            }),
    );

    ngOnInit() {
        this.platformSettingService
            .getIntegrations()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ result }) => this.integrations.set(result ?? []),
                error: () => this.integrations.set([]),
            });
    }

    protected readonly profileMenu = computed((): MenuItem[] => {
        const menu: MenuItem[] = [];
        menu.push({
            label: 'Support',
            items: [
                ...(this.productFruitsId()
                    ? [
                          {
                              label: 'Learning Hub',
                              icon: 'pi pi-graduation-cap',
                              command: () => this.learningHubService.openDialog(),
                          },
                      ]
                    : []),
                {
                    label: 'Documentation',
                    icon: 'pi pi-book',
                    url: 'https://docs.genguardx.ai',
                    target: '_blank',
                },
                { separator: true },
            ],
        });

        if (this.ideMenuItems().length > 0) {
            menu.push({
                label: 'IDEs',
                items: [...this.ideMenuItems(), { separator: true }],
            });
        }
        menu.push({
            label: 'Profile',
            items: [
                {
                    label: 'Org Settings',
                    icon: 'pi pi-cog',
                    routerLink: [
                        '',
                        {
                            outlets: {
                                settings: [
                                    MODULE_ROUTE_PATH.SETTING,
                                    PAGE_ROUTE_PATH.USER_MANAGEMENT,
                                ],
                            },
                        },
                    ],
                },
                {
                    label: 'My Account',
                    icon: 'pi pi-user',
                    routerLink: [
                        '',
                        {
                            outlets: {
                                settings: [MODULE_ROUTE_PATH.SETTING, TAB_ROUTES.MY_ACCOUNT],
                            },
                        },
                    ],
                },
                {
                    label: 'Logout',
                    icon: 'pi pi-sign-out',
                    routerLink: `/logout`,
                    labelClass: 'tw:text-red-500',
                    iconClass: 'tw:text-red-500',
                },
            ],
        });

        return menu;
    });

    protected copyApiKey(): void {
        if (!this.profileApiKey.apiKey() || this.profileApiKey.copyStatus() !== 'isIdle') return;

        this.clipboardService.copyWithStatus(
            this.profileApiKey.apiKey(),
            this.profileApiKey.copyStatus,
            this.destroyRef,
        );
    }

    private getIDEUrl(url: string, queryParams: string[]): string {
        const currentUser = this.authService.currentUser();
        const paramMap: Record<string, string> = {
            userid: currentUser?.id?.toString() ?? '',
            usertoken: this.authService.getToken() ?? '',
            username: currentUser?.name ?? '',
            useremail: currentUser?.email ?? '',
        };

        const searchParams = new URLSearchParams(
            Object.fromEntries(queryParams.map(key => [key, paramMap[key] ?? ''])),
        );

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${searchParams.toString()}`;
    }
}
